import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Text } from '@pixi/react';

import { gameLiveConnection } from '../../../services/socketConnection/gameConnection';

import { isMobile } from '../../../core/helpers/isMobile';
import { formatTime } from '../../../core/helpers/formatTime';

import { jackPotStore } from '../../../store/jackPotStore';
import { applicationStore } from '../../../store/applicationStore';
import { gameService } from '../../../services/gameService';

const ExpireDate = observer(({ x, y, isBoosted = false }) => {
  const { jackPotExpireTime, setJackPotExpireTime, setIsAllowDoJackpotAction } = jackPotStore;
  const [formattedTime, setFormattedTime] = useState(formatTime(jackPotExpireTime));
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState(Date.now());
  const [lastTabSwitchTime, setLastTabSwitchTime] = useState(null);

  // Main timer effect
  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentTime = Date.now();
      const elapsed = currentTime - lastUpdateTimestamp;

      // Update jackpot expire time
      const newExpireTime = Math.max(jackPotExpireTime - elapsed, 0);
      setJackPotExpireTime(newExpireTime);
      // If jackpot time reaches zero, call the jackpot scene
      if (newExpireTime === 0) {
        if (!isBoosted) {
          gameLiveConnection.getJackpotScene();
        } else {
          gameService.getBoostedJackpot();
        }
        clearInterval(intervalId); // Clear the interval when time runs out
      }

      // Update the last timestamp
      setLastUpdateTimestamp(currentTime);
    }, 1000);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [isBoosted, jackPotExpireTime, lastUpdateTimestamp, setJackPotExpireTime]);

  // Handle tab visibility change to recalculate expire time accurately
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, record the time
        setLastTabSwitchTime(Date.now());
      } else if (lastTabSwitchTime !== null) {
        // Tab is visible again, calculate the elapsed time
        const deltaTime = (Date.now() - lastTabSwitchTime) / 1000; // Time in seconds
        // Directly calculate and update the expire time
        setJackPotExpireTime(jackPotExpireTime - deltaTime); // Adjust expire time
        setLastTabSwitchTime(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastTabSwitchTime, jackPotExpireTime, setJackPotExpireTime]);

  // Update formatted time when expire time changes
  useEffect(() => {
    setFormattedTime(formatTime(jackPotExpireTime));
  }, [jackPotExpireTime]);

  // Adjust actions based on expire time
  useEffect(() => {
    if (jackPotExpireTime < 3000 && !isBoosted) {
      setIsAllowDoJackpotAction(false);
    }
  }, [isBoosted, jackPotExpireTime, setIsAllowDoJackpotAction]);

  if (isBoosted) {
    return null;
  }

  return (
    <Container x={x} y={y} name='JackPotExpireTimeContainer'>
      <Text
        anchor={0.5}
        resolution={2}
        text={`${applicationStore.canvasTranslationKeys.EXPIRY} ${
          jackPotExpireTime > 0 ? formattedTime : '00:00'
        }`}
        style={{ _fill: '#FFFFFF', _fontSize: isMobile() ? 13 : 16 }}
      />
    </Container>
  );
});

export default ExpireDate;
